import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa um perfil profissional com suas informações e custos médios
 * 
 * @typedef {Object} Perfil
 * @property {number} id - Identificador único do perfil
 * @property {string} especialidade - Área de atuação profissional
 * @property {string} senioridade - Nível de senioridade do profissional
 * @property {CustoMedio[]|null} custos_medios - Lista de custos médios associados ao perfil (pode ser nulo)
 * @property {CustoMedio|null} custo_medio - Custo médio atual/principal do perfil (pode ser nulo)
 */

/**
 * Representa um custo médio de um perfil profissional
 * 
 * @typedef {Object} CustoMedio
 * @property {number} id - Identificador único do custo médio
 * @property {number} perfil_id - ID do perfil ao qual este custo está associado
 * @property {string} valor - Valor do custo médio no formato "2500.00"
 * @property {string} competencia_inicio - Data de início de vigência no formato "YYYY-MM"
 * @property {string} competencia_fim - Data de fim de vigência no formato "YYYY-MM"
 */

const resource = `v1/ativacaoped/:empresaId/perfil`;
const LOADING_KEY = 'ativacaoped-perfil';

/**
 * Lista perfis com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<Perfil>>} Lista paginada de perfis
 */
export const getPerfis = (empresaId, params = { page: 1, per_page: 10 }) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista perfis simplificados para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<Perfil[]>} Lista de perfis
 */
export const getSelecaoPerfis = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId).concat(`/selecao`);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um perfil específico pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} perfilId - ID do perfil
 * @returns {Promise<Perfil>} Dados do perfil
 */
export const getPerfil = (empresaId, perfilId) => {
  const baseUrl = resource.replace(":empresaId", empresaId).concat(`/${perfilId}`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data.data)
  );
};

/**
 * Cria um novo perfil.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} data - Dados do perfil a ser criado
 * @param {string} data.senioridade - Nível de senioridade (obrigatório)
 * @param {string} data.especialidade - Área de atuação (obrigatório)
 * @param {CustoMedio[]} [data.custos_medios] - Lista de custos médios
 * @param {CustoMedio} [data.custo_medio] - Custo médio atual
 * @returns {Promise<Perfil>} Perfil criado
 */
export const postPerfil = (empresaId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data)
  );
}

/**
 * Atualiza um perfil existente.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} perfilId - ID do perfil a ser atualizado
 * @param {Partial<Perfil>} data - Dados parciais do perfil para atualização
 * @returns {Promise<Perfil>} Perfil atualizado
 */
export const putPerfil = (empresaId, perfilId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.post(`${baseUrl}/${perfilId}`, data).then(({ data }) => data.data)
  );
}

/**
 * Exclui um perfil.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} perfilId - ID do perfil a ser excluído
 * @returns {Promise<void>}
 */
export const deletePerfil = (empresaId, perfilId) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.delete(`${baseUrl}/${perfilId}`)
  );
}

export default {
  getPerfil,
  getPerfis,
  getSelecaoPerfis,
  postPerfil,
  putPerfil,
  deletePerfil
};
